import { useEffect, useMemo, useState } from "react";

import Col from "react-bootstrap/Col";
import Row from "react-bootstrap/Row";
import Button from "react-bootstrap/Button";

import { useCurrentAccount } from "@/hooks/useCurrentAccount";
import { ModuleEnum } from "@/utils/enums/ModuleEnum";
import { getAllProfiles } from "@/services/app/profileService";
import { CityEsusRoleEnum, CitySIntegradaRoleEnum } from "@/utils/enums/RoleEnum";
import { UsersAccountAccess } from "@/services/app/userAccountService";
import { Restrict } from "@/components/Restrict";
import { filteredRoleByModule } from "@/utils/app/filteredRoleByModule";
import { useProfessionalAccessData } from "@/hooks/useProfessionalAccessData";
import { Role } from "@/@types/app/Role";
import { Option } from "@/@types/generics/Option";

import { CitiesSelect } from "@/components/CitiesSelect";
import { FederalUnitsSelect } from "@/components/FederalUnitsSelect";
import { ModuleSelect } from "@/components/ModuleSelect";
import { ProfileSelect } from "@/components/ProfileSelect";

type Props = {
	selectedUser: UsersAccountAccess;

	selectedOptionModuleModal?: Option<ModuleEnum | undefined>;
	setSelectedOptionModuleModal: (value: Option<ModuleEnum | undefined>) => void;

	selectedOptionFederalUnitModal: Option<string>;
	setSelectedOptionFederalUnitModal: (value: Option<string>) => void;

	selectedOptionCityModal: Option<string>;
	setSelectedOptionCityModal: (value: Option<string>) => void;

	selectedOptionRoleModal: Option<string>;
	setSelectedOptionRoleModal: (value: Option<string>) => void;

	selectedCnesList: string[];
	setSelectedCnesList: (cnesList: string[]) => void;

	handleUpdateProfessionalAccess: () => void;
	fetchUnitsPaginated: () => void;
};

export function ProfessionalUnitAccessFilters({
	selectedUser,
	selectedOptionModuleModal,
	setSelectedOptionModuleModal,
	selectedOptionFederalUnitModal,
	setSelectedOptionFederalUnitModal,
	selectedOptionCityModal,
	setSelectedOptionCityModal,
	selectedOptionRoleModal,
	setSelectedOptionRoleModal,
	selectedCnesList,
	handleUpdateProfessionalAccess,
	fetchUnitsPaginated,
}: Props) {
	const { user, module, permissions, uf } = useCurrentAccount();
	const { modules, federalUnits, cities } = useProfessionalAccessData(
		selectedOptionModuleModal?.value,
		selectedOptionFederalUnitModal?.value
	);

	const [roles, setRoles] = useState<Role[]>([]);

	const isFederalUnitsSelectDisabled = !user.isAdmin;
	const isCitiesSelectDisabled = !user.isAdmin;
	const isModuleSelectionDisabled = !user.isAdmin;

	const filteredFederalUnits = federalUnits.filter(
		(federalUnit) =>
			federalUnit.abbreviation === selectedOptionFederalUnitModal?.value || user.isAdmin
	);

	const filteredRoles = filteredRoleByModule(
		user.isAdmin,
		roles,
		permissions,
		selectedOptionModuleModal?.value || module
	);

	async function handleFetchProfiles(selectedModule?: Option<ModuleEnum | undefined>) {
		setRoles(await getAllProfiles(selectedModule?.value || module));
	}

	const isSaveButtonDisabled = useMemo(() => {
		const isMissingRequiredFields =
			!(selectedOptionModuleModal?.value || module) ||
			!selectedUser.userId ||
			!selectedOptionRoleModal?.value ||
			!(selectedOptionFederalUnitModal?.value || uf);

		const isEsusProfessionalWithoutCnesList =
			selectedOptionRoleModal?.value.toString() ===
				CityEsusRoleEnum.ESUS_PROFESSIONAL.toString() && selectedCnesList.length === 0;

		const isSintegradaProfessionalWithoutCnesList =
			selectedOptionRoleModal?.value.toString() ===
				CitySIntegradaRoleEnum.SINTEGRADA_PROFESSIONAL.toString() &&
			selectedCnesList.length === 0;

		return (
			isMissingRequiredFields ||
			isEsusProfessionalWithoutCnesList ||
			isSintegradaProfessionalWithoutCnesList
		);
	}, [
		module,
		uf,
		selectedOptionModuleModal?.value,
		selectedOptionRoleModal?.value,
		selectedUser?.userId,
		selectedOptionFederalUnitModal?.value,
		selectedCnesList,
	]);

	useEffect(() => {
		handleFetchProfiles(selectedOptionModuleModal);
		setSelectedOptionRoleModal({ value: "", label: "Selecione" } as Option<string>);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedOptionModuleModal?.value]);

	useEffect(() => {
		if (
			selectedOptionRoleModal.value.toString() ===
				CitySIntegradaRoleEnum.SINTEGRADA_PROFESSIONAL.toString() ||
			selectedOptionRoleModal.value.toString() ===
				CityEsusRoleEnum.ESUS_PROFESSIONAL.toString()
		) {
			fetchUnitsPaginated();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedOptionRoleModal]);

	return (
		<Row className="d-flex mb-5">
			<Restrict>
				<Col
					sm={2}
					md={2}
					lg={2}
					xxl={2}
					className="d-flex flex-column justify-content-start"
				>
					<ModuleSelect
						modules={modules}
						value={selectedOptionModuleModal}
						onChange={(value) => {
							const newValue = value as Option<number>;
							setSelectedOptionModuleModal(newValue as Option<number>);
							handleFetchProfiles(newValue);
						}}
						isDisabled={isModuleSelectionDisabled}
					/>
				</Col>
			</Restrict>
			<Col sm={2} md={2} lg={2} xxl={2} className="d-flex flex-column justify-content-start">
				<FederalUnitsSelect
					value={selectedOptionFederalUnitModal}
					federalUnits={filteredFederalUnits}
					isDisabled={isFederalUnitsSelectDisabled}
					onChange={(event) => {
						setSelectedOptionFederalUnitModal(event as Option<string>);
						setSelectedOptionCityModal({
							label: "Todas as cidades",
							value: "",
						} as Option<string>);
					}}
				/>
			</Col>
			<Col sm={10} md={3} lg={3} xxl={3} className="d-flex flex-column justify-content-start">
				<CitiesSelect
					value={selectedOptionCityModal}
					accounts={cities}
					isDisabled={isCitiesSelectDisabled}
					onChange={(event) => {
						setSelectedOptionCityModal(event as Option<string>);
					}}
				/>
			</Col>

			<Col sm={12} md={3} lg={3} xxl={3} className="d-flex flex-column justify-content-start">
				<ProfileSelect
					roles={filteredRoles}
					value={selectedOptionRoleModal}
					onChange={(event) => {
						setSelectedOptionRoleModal(event as Option<string>);
					}}
				/>
			</Col>
			<Col
				sm={12}
				md={2}
				lg={2}
				xxl={2}
				className="d-flex align-items-end justify-content-start"
			>
				<Button
					className="mt-2"
					variant="success"
					onClick={() => handleUpdateProfessionalAccess()}
					disabled={isSaveButtonDisabled}
				>
					Salvar
				</Button>
			</Col>
		</Row>
	);
}
